import { ofType } from 'redux-observable';
import { Observable, from, of } from 'rxjs';
import { catchError, distinctUntilChanged, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Users } from '@bridebook/models';
import { updateAccessControlSuccess } from '@bridebook/web/lib/access-control/actions';
import { appError } from '@bridebook/web/lib/app/actions';
import { IEpicDeps } from '@bridebook/web/lib/types';
import { IOnUserListenerAction, UserActionTypes } from '@bridebook/web/lib/users/action-types';
import { recursiveSerializeTimestamps } from '@bridebook/toolbox/src/serialize-timestamps';
import { recursiveOmitMutableTimestamps } from '@bridebook/web/lib/utils';

export const readAccessControl = (
  action$: Observable<IOnUserListenerAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(UserActionTypes.ON_USER_LISTENER),
    withLatestFrom(state$),
    distinctUntilChanged(
      ([{ payload: previousUser }], [{ payload: nextUser }, state]) =>
        previousUser !== null &&
        nextUser !== null &&
        previousUser.id === nextUser.id &&
        !!state.weddings.profile.id,
    ),
    mergeMap(([{ payload: user }, state]) => {
      if (!user?.id) {
        return of();
      }

      const getPromise = async () => {
        const userEntity = Users._.getById(user.id);
        const activeWeddingRaw = await userEntity.getActiveWedding();
        const activeWedding = activeWeddingRaw
          ? recursiveOmitMutableTimestamps(recursiveSerializeTimestamps(activeWeddingRaw), [
              'draft',
            ])
          : null;
        const { profile } = state.weddings;
        const needToUpdateProfileState = !profile?.id;

        return { activeWedding, needToUpdateProfileState };
      };

      return from(getPromise()).pipe(
        mergeMap(({ needToUpdateProfileState, activeWedding }) => {
          if (needToUpdateProfileState && activeWedding) {
            return of(updateAccessControlSuccess(activeWedding));
          }

          return of();
        }),
        catchError((error: Error) => of(appError({ error, feature: 'Read Access Control' }))),
      );
    }),
    catchError((error: Error) => of(appError({ error, feature: 'Read Access Control' }))),
  );
